import React, { ChangeEvent, useState } from "react";
import { Table } from "evergreen-ui";
import { TableCellChangedHandler } from "../factory";
import { ColumnConfig } from "../config/ColumnConfig";

interface TableEditableCellProps<T> {
    columnConfig: ColumnConfig<T>;
    id: string | number;
    cellValue: string;
    onCellChanged?: TableCellChangedHandler<T>;
    inputType: "number" | "text";
}

export const TableInputCell = <T,>({
    columnConfig,
    id,
    cellValue,
    onCellChanged,
    inputType,
}: TableEditableCellProps<T>) => {

    // Used to distinguish between an escape key press and a blur event.
    const escapeKeyPressedRef = React.useRef<boolean>(false);

    const [unsubmittedValue, setUnsubmittedValue ] = useState<string>(cellValue);

    const resolveCellValue = (value: string): string | number => {
        switch (inputType) {
            case "number":
                return parseFloat(value);
            case "text":
            default:
                return value;
        }
    }

    const submitValueIfNeeded = () => {
        if (cellValue === unsubmittedValue || escapeKeyPressedRef.current) {
            return;
        }

        if (onCellChanged) {
            // The handler returns the resolved value from the page's
            // onUpdateRow. If the edit was rejected, this is the original
            // value. If accepted and normalized (e.g. "us" -> "US"), this
            // is the normalized value.
            const resolved = onCellChanged(
                id,
                columnConfig.columnName as keyof T,
                resolveCellValue(unsubmittedValue),
            );
            setUnsubmittedValue(String(resolved ?? ""));
        }
    };

    return (
        <Table.Cell className={"jc-body-cell input"}>
            <input
                type={inputType}
                value={unsubmittedValue}
                style={columnConfig.textAlign ? { textAlign: columnConfig.textAlign } : undefined}
                onChange={({ target : { value } }: ChangeEvent<HTMLInputElement>) => {
                    setUnsubmittedValue(value);
                }}
                onBlur={() => {
                    // Update the table cell with the new value.
                    submitValueIfNeeded();

                    // Reset the escape key flag, so that the next onBlur can submit the cell value.
                    escapeKeyPressedRef.current = false;
                }}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (event.key === "Escape") {
                        // Reset the input value to the initial value.
                        setUnsubmittedValue(cellValue);

                        // Indicate that the escape key is triggering the onBlur event.
                        escapeKeyPressedRef.current = true;

                        // Remove focus from the input (the onBlur callback will be fired).
                        event.currentTarget.blur();
                    } else if (event.key === "Enter") {
                        // Just blur — the onBlur handler will call submitValueIfNeeded().
                        // Calling submit here AND blurring would cause a double-submit
                        // where React defers the second setData updater evaluation,
                        // returning the wrong (unresolved) value.
                        event.currentTarget.blur();
                    }
                }}
            />
        </Table.Cell>
    )
}
